import { Node, mergeAttributes } from '@tiptap/react';

/**
 * A pasted screenshot, inline in the document.
 *
 * ── The one thing to understand before changing this ────────────────────────
 * The node stores an ATTACHMENT ID, not a URL. An attachment's URL is a
 * presigned S3 GET that expires in ten minutes, so a document holding one
 * would render for whoever pasted it and show a broken image to everyone who
 * opened the request the next day. The server refuses any attribute other than
 * `attachmentId` and `alt` (see message-body.ts's `parseImage`), so this is
 * enforced rather than merely intended.
 *
 * `src` exists as an attribute all the same, and is deliberately transient:
 * while an upload is in flight there IS no attachment id yet, only a local
 * blob URL, and showing the image immediately is the entire point. It is
 * declared `rendered: false` so it never reaches the stored JSON's attrs... it
 * would, in fact — `getJSON()` serialises every attribute regardless. So
 * `stripTransientAttrs` below is what actually removes it, and it is called on
 * the way to the server. Both halves are needed: this one so the editor can
 * hold a blob URL, that one so the document never carries it.
 */

export interface ImageAttrs {
  attachmentId: string | null;
  /** Local object URL while uploading; removed before the document is saved. */
  src: string | null;
  alt: string;
}

export const ImageNode = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      attachmentId: { default: null },
      src: { default: null },
      alt: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'img[data-attachment-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = HTMLAttributes as Partial<ImageAttrs>;
    return [
      'img',
      mergeAttributes({
        'data-attachment-id': attrs.attachmentId ?? undefined,
        src: attrs.src ?? undefined,
        alt: attrs.alt ?? '',
        class: 'max-h-[24rem] max-w-full rounded-[8px] border border-line',
      }),
    ];
  },
});

/**
 * Remove everything the server will not accept, and drop what it cannot use.
 *
 * Two jobs, both on the way out:
 *
 *  - `src` goes. It is a blob URL belonging to one browser tab.
 *  - An image with no `attachmentId` goes entirely. That is an upload which
 *    failed or had not finished, and a node pointing at nothing would be
 *    rejected by the server — losing the whole save, rather than the one image
 *    that did not make it.
 *
 * Shallow rather than recursive on purpose: `image` is a top-level block and
 * cannot appear inside a list item or a code block, so there is nowhere else
 * to look and a recursive walk would imply otherwise.
 */
export function stripTransientAttrs(doc: unknown): unknown {
  if (typeof doc !== 'object' || doc === null) return doc;
  const record = doc as { content?: unknown[] };
  if (!Array.isArray(record.content)) return doc;

  const content = record.content.flatMap((node) => {
    if (typeof node !== 'object' || node === null) return [node];
    const block = node as { type?: string; attrs?: Record<string, unknown> };
    if (block.type !== 'image') return [node];

    const attachmentId = block.attrs?.['attachmentId'];
    if (typeof attachmentId !== 'string') return [];

    const alt = block.attrs?.['alt'];
    return [{ type: 'image', attrs: { attachmentId, alt: typeof alt === 'string' ? alt : '' } }];
  });

  return { ...record, content };
}

/**
 * The attachment ids a document shows inline.
 *
 * An inlined image is still a real attachment row — the node points at it, and
 * that is how its URL is resolved. But it must not ALSO appear in the
 * attachments strip underneath, or every pasted screenshot shows up twice: once
 * where it was put, and once as a file that looks like something separate.
 *
 * The document is the source of truth for what is inline, rather than a flag on
 * the attachment: the same image can be inlined, then deleted from the text, and
 * the right answer changes with the text and nothing else has to be kept in
 * step.
 *
 * Shallow, matching `stripTransientAttrs` — `image` is a top-level block and
 * cannot appear inside a list item or a code block.
 */
export function inlineAttachmentIds(doc: unknown): ReadonlySet<string> {
  const ids = new Set<string>();
  if (typeof doc !== 'object' || doc === null) return ids;
  const content = (doc as { content?: unknown }).content;
  if (!Array.isArray(content)) return ids;

  for (const node of content) {
    if (typeof node !== 'object' || node === null) continue;
    const block = node as { type?: string; attrs?: Record<string, unknown> };
    if (block.type !== 'image') continue;
    const id = block.attrs?.['attachmentId'];
    if (typeof id === 'string') ids.add(id);
  }
  return ids;
}
